/**
 * Forms for the seven methods that need figures the accounts cannot hold.
 *
 * Built from `METHOD_SPECS` rather than written seven times over, so the fields, their
 * labels and their units have one home and adding a method is an entry in a table.
 *
 * Three decisions worth stating, because each is the opposite of the obvious one:
 *
 * CLOSED UNTIL ASKED FOR. Seven forms open at once would bury the discounted cash flow
 * that is the answer for most companies. A method opens when its own row is clicked,
 * and one that has figures in it already opens by itself.
 *
 * SAVED PER METHOD, NOT PER KEYSTROKE. Each valuation runs the whole engine, so a save
 * on every character would be a valuation on every character. An explicit save per
 * method also makes a half-filled form harmless: nothing is sent until it is asked
 * for.
 *
 * AN INCOMPLETE METHOD SAYS WHAT IS MISSING, AND NOT NOUGHT. The backend returns a row
 * with `needs` set, and that is shown where the value would be. A company worth zero
 * sitting beside methods that worked is indistinguishable from an answer.
 */

import { useEffect, useState } from 'react';
import DescribeMethodInputs from './DescribeMethodInputs';
import PriceScenarios, { type PriceScenario } from './PriceScenarios';
import {
  METHOD_SPECS,
  UNIT_SUFFIX,
  toStored,
  toTyped,
  type MethodField,
  type MethodSpec,
} from './methodSpecs';

/**
 * One method's result.
 *
 * The shape `standalone.value_figures` reports as `suppliedMethods` AND the shape a
 * model's `methods.results` already uses. Exported because both callers hand these in.
 */
export interface SuppliedMethod {
  method: string;
  label: string;
  equityValue: number | null;
  perShare: number | null;
  basis: string;
  needs: string | null;
  /** The commodity methods' answer along other price paths. */
  scenarios?: PriceScenario[];
}

function money(value: number | null): string {
  if (value === null) return '—';
  return value.toLocaleString(undefined, { maximumFractionDigits: 0 });
}

/** A block as it is held while being edited: strings, because that is what was typed. */
type Draft = {
  fields: Record<string, string>;
  list: Record<string, string>[];
};

function draftFrom(spec: MethodSpec, saved: unknown): Draft {
  const block = (saved ?? {}) as Record<string, unknown>;
  const fields: Record<string, string> = {};
  for (const field of spec.fields) {
    fields[field.key] = toTyped(field.kind, block[field.key]);
  }

  let list: Record<string, string>[] = [];
  if (spec.list) {
    const rows = (block[spec.list.key] as Record<string, unknown>[]) ?? [];
    list = rows.map((row) => {
      const out: Record<string, string> = {};
      for (const column of spec.list!.columns) {
        out[column.key] = toTyped(column.kind, row[column.key]);
      }
      return out;
    });
  }
  return { fields, list };
}

function blankRow(spec: MethodSpec): Record<string, string> {
  const row: Record<string, string> = {};
  for (const column of spec.list?.columns ?? []) row[column.key] = '';
  return row;
}

/**
 * A draft, as the backend wants it.
 *
 * Blank fields are LEFT OUT rather than sent as zero. A cap rate omitted must make the
 * method say it needs one; sent as nought it would divide by zero, or worse, look like
 * a deliberate nil.
 */
function payloadFrom(spec: MethodSpec, draft: Draft): Record<string, unknown> | null {
  const block: Record<string, unknown> = {};

  for (const field of spec.fields) {
    const value = toStored(field.kind, draft.fields[field.key] ?? '');
    if (value !== null && value !== '') block[field.key] = value;
  }

  if (spec.list) {
    const rows: Record<string, unknown>[] = [];
    for (const row of draft.list) {
      const entry: Record<string, unknown> = {};
      let hasFigure = false;
      for (const column of spec.list.columns) {
        const value = toStored(column.kind, row[column.key] ?? '');
        if (value === null || value === '') continue;
        entry[column.key] = value;
        if (column.kind !== 'text') hasFigure = true;
      }
      // A row with a name and no figures is someone who started typing, not an asset
      // worth nothing.
      if (hasFigure) rows.push(entry);
    }
    if (rows.length > 0) block[spec.list.key] = rows;
  }

  return Object.keys(block).length > 0 ? block : null;
}

function Cell({
  field,
  value,
  onChange,
}: {
  field: MethodField;
  value: string;
  onChange: (next: string) => void;
}) {
  const suffix = UNIT_SUFFIX[field.kind];
  if (field.kind === 'choice') {
    return (
      // Unset shows the first option, which is the backend's own default: a blank "Default"
      // said nothing about which way the prices were being drawn.
      <select
        value={value || field.options?.[0]?.value || ''}
        title={field.hint}
        onChange={(event) => onChange(event.target.value)}
        className="rounded border border-gray-300 px-1.5 py-1 text-sm"
      >
        {(field.options ?? []).map((option) => (
          <option key={option.value} value={option.value}>
            {option.label}
          </option>
        ))}
      </select>
    );
  }
  return (
    <div className="flex items-center gap-1">
      <input
        value={value}
        title={field.hint}
        placeholder={field.kind === 'text' ? 'Name' : ''}
        inputMode={field.kind === 'text' ? 'text' : 'decimal'}
        onChange={(event) => onChange(event.target.value)}
        className={`rounded border border-gray-300 px-1.5 py-1 text-sm ${
          field.kind === 'text'
            ? 'w-40'
            : 'w-28 text-right tabular-nums'
        }`}
      />
      {suffix && <span className="text-xs text-gray-400">{suffix}</span>}
    </div>
  );
}

function MethodForm({
  spec,
  saved,
  result,
  isSaving,
  onSave,
  onClear,
}: {
  spec: MethodSpec;
  saved: unknown;
  result: SuppliedMethod | undefined;
  isSaving: boolean;
  onSave: (block: Record<string, unknown> | null) => void;
  onClear: () => void;
}) {
  const [draft, setDraft] = useState<Draft>(() => draftFrom(spec, saved));

  // Re-read when the saved block changes underneath — a refresh from the accounts, or
  // a spreadsheet upload filling these in.
  useEffect(() => {
    setDraft(draftFrom(spec, saved));
  }, [spec, saved]);

  const setField = (key: string, next: string) =>
    setDraft((current) => ({
      ...current,
      fields: { ...current.fields, [key]: next },
    }));

  const setListCell = (index: number, key: string, next: string) =>
    setDraft((current) => ({
      ...current,
      list: current.list.map((row, position) =>
        position === index ? { ...row, [key]: next } : row,
      ),
    }));

  return (
    <div className="border-t border-gray-100 bg-gray-50/50 px-4 py-4">
      <p className="mb-3 max-w-2xl text-xs leading-relaxed text-gray-600">
        {spec.about}
      </p>

      {spec.fields.length > 0 && (
        <div className="flex flex-wrap gap-4">
          {spec.fields.map((field) => (
            <label key={field.key} className="text-xs font-medium text-gray-700">
              {field.label}
              <div className="mt-1">
                <Cell
                  field={field}
                  value={draft.fields[field.key] ?? ''}
                  onChange={(next) => setField(field.key, next)}
                />
              </div>
              {field.hint && (
                <span className="mt-1 block max-w-[16rem] font-normal text-gray-500">
                  {field.hint}
                </span>
              )}
            </label>
          ))}
        </div>
      )}

      {spec.list && (
        <div className="mt-4">
          <table className="border-collapse text-xs">
            <thead>
              <tr>
                {spec.list.columns.map((column) => (
                  <th
                    key={column.key}
                    title={column.hint}
                    className="px-1 py-1 text-left font-medium text-gray-500"
                  >
                    {column.label}
                  </th>
                ))}
                <th />
              </tr>
            </thead>
            <tbody>
              {draft.list.map((row, index) => (
                <tr key={index}>
                  {spec.list!.columns.map((column) => (
                    <td key={column.key} className="px-1 py-1">
                      <Cell
                        field={column}
                        value={row[column.key] ?? ''}
                        onChange={(next) => setListCell(index, column.key, next)}
                      />
                    </td>
                  ))}
                  <td className="px-1 py-1">
                    <button
                      type="button"
                      aria-label={`Remove this ${spec.list!.noun}`}
                      onClick={() =>
                        setDraft((current) => ({
                          ...current,
                          list: current.list.filter((_, p) => p !== index),
                        }))
                      }
                      className="rounded px-1.5 py-1 text-gray-400 hover:bg-gray-200 hover:text-gray-700"
                    >
                      ×
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          <button
            type="button"
            onClick={() =>
              setDraft((current) => ({
                ...current,
                list: [...current.list, blankRow(spec)],
              }))
            }
            className="mt-2 rounded border border-gray-300 px-2.5 py-1 text-xs text-gray-700 hover:bg-white"
          >
            Add {/^[aeiou]/i.test(spec.list.noun) ? 'an' : 'a'} {spec.list.noun}
          </button>
        </div>
      )}

      <div className="mt-4 flex items-center gap-2">
        <button
          type="button"
          disabled={isSaving}
          onClick={() => onSave(payloadFrom(spec, draft))}
          className="rounded bg-gray-900 px-3 py-1.5 text-xs text-white disabled:opacity-40"
        >
          {isSaving ? 'Valuing…' : 'Apply'}
        </button>
        {saved != null && (
          <button
            type="button"
            disabled={isSaving}
            onClick={onClear}
            className="rounded border border-gray-300 px-2.5 py-1.5 text-xs text-gray-700 hover:bg-white disabled:opacity-40"
          >
            Remove this method
          </button>
        )}
      </div>

      {result?.needs && (
        <p className="mt-3 text-xs text-amber-800">
          Not valued yet — this method still needs {result.needs}.
        </p>
      )}
      {result?.basis && (
        <p className="mt-3 max-w-2xl text-xs leading-relaxed text-gray-600">
          {result.basis}
        </p>
      )}
      <PriceScenarios scenarios={result?.scenarios} format={(value) => money(value)} />
    </div>
  );
}

export default function ValuationMethodInputs({
  methodInputs,
  results,
  isSaving,
  onChange,
}: {
  /** The saved bag, whatever holds it. */
  methodInputs: Record<string, unknown> | null | undefined;
  /**
   * What each method came to, where it ran.
   *
   * The two callers get these from different places and the shapes already agree: a
   * standalone valuation's `computed.suppliedMethods` and a model's
   * `computed.methods.results` carry the same six fields. That is why this takes the
   * rows rather than a project - it used to take a whole `ValuationProject`, which
   * made it unreachable on a MODEL, where the only way to enter a property valuation
   * or a pipeline is the valuation settings page.
   */
  results: SuppliedMethod[];
  isSaving: boolean;
  /** Replaces the whole `methodInputs` bag. The engine re-runs on the answer. */
  onChange: (methodInputs: Record<string, unknown> | null) => void;
}) {
  const saved = (methodInputs ?? {}) as Record<string, unknown>;
  const byMethod = new Map(results.map((row) => [row.method, row]));

  // A method with figures in it is open and the rest are closed, so seven forms do not
  // bury the cash flow that is the answer for most companies. Clicking one records a
  // decision about THAT method only: a single "which one is open" would close a
  // supplied method because a different one was opened, which reads as the figures
  // having been lost.
  const [toggled, setToggled] = useState<Record<string, boolean>>({});

  const write = (block: string, value: Record<string, unknown> | null) => {
    const next: Record<string, unknown> = { ...saved };
    if (value === null) delete next[block];
    else next[block] = value;
    onChange(Object.keys(next).length > 0 ? next : null);
  };

  return (
    <section className="mt-8">
      <h2 className="text-sm font-semibold text-gray-900">Other methods</h2>
      <p className="mt-1 max-w-2xl text-xs leading-relaxed text-gray-600">
        Each of these needs a figure a set of accounts does not contain — a property
        valuation, a policy book, a drug programme’s peak sales. Fill one in and it is
        valued beside the cash flow above; leave it alone and it stays out of the way.
        A spreadsheet upload fills in whatever it recognised — or describe them in
        words below.
      </p>

      {/* Before the forms, because it is the quicker road for most people and the forms
          are where its output lands. Accepting a parse MERGES over what is already
          saved: someone who entered a NAV by hand and then describes a pipeline should
          end with both. */}
      <DescribeMethodInputs
        onAccept={(parsed) => onChange({ ...saved, ...parsed })}
      />

      <div className="mt-3 divide-y divide-gray-100 rounded border border-gray-200">
        {METHOD_SPECS.map((spec) => {
          const block = saved[spec.block];
          const result = byMethod.get(spec.method);
          const isOpen = toggled[spec.block] ?? block != null;

          return (
            <div key={spec.block}>
              <button
                type="button"
                onClick={() =>
                  setToggled((current) => ({ ...current, [spec.block]: !isOpen }))
                }
                aria-expanded={isOpen}
                className="flex w-full items-center justify-between gap-3 px-4 py-2.5 text-left hover:bg-gray-50"
              >
                <span className="flex min-w-0 items-center gap-2">
                  <span aria-hidden="true" className="text-xs text-gray-400">
                    {isOpen ? '▾' : '▸'}
                  </span>
                  <span className="truncate text-sm font-medium text-gray-800">
                    {spec.label}
                  </span>
                  {block == null && (
                    <span className="shrink-0 text-xs text-gray-400">not supplied</span>
                  )}
                </span>
                <span className="shrink-0 text-right">
                  {result?.equityValue != null ? (
                    <>
                      <span className="text-sm font-semibold tabular-nums text-gray-900">
                        {money(result.equityValue)}
                      </span>
                      {result.perShare != null && (
                        <span className="ml-2 text-xs tabular-nums text-gray-500">
                          {result.perShare.toFixed(2)} / share
                        </span>
                      )}
                    </>
                  ) : (
                    result?.needs && (
                      <span className="text-xs text-amber-700">needs more</span>
                    )
                  )}
                </span>
              </button>

              {isOpen && (
                <MethodForm
                  spec={spec}
                  saved={block}
                  result={result}
                  isSaving={isSaving}
                  onSave={(value) => write(spec.block, value)}
                  onClear={() => write(spec.block, null)}
                />
              )}
            </div>
          );
        })}
      </div>
    </section>
  );
}
